<template>
  <TransitionRoot appear :show="isTrialModalOpen" as="template">
    <Dialog as="div" class="relative z-50" @close="closeModal">
      <TransitionChild as="template" enter="duration-200 ease-out" enter-from="opacity-0" enter-to="opacity-100"
        leave="duration-150 ease-in" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-black/60 backdrop-blur-md" />
      </TransitionChild>

      <div class="fixed inset-0 flex items-center justify-center p-4">
        <TransitionChild as="template" enter="duration-200 ease-out" enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100" leave="duration-150 ease-in" leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95">
          <DialogPanel
            class="w-full max-w-md rounded-2xl bg-nera-elevated-1 border border-nera-light-border p-8 text-center shadow-2xl">
            <template v-if="trialState === 'confirm'">
              <div
                class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500 text-xl">
                <i class="fas fa-gift"></i>
              </div>
              <DialogTitle class="text-xl font-semibold text-nera-text-light">
                Start your free trial
              </DialogTitle>
              <p class="mt-2 mb-8! text-sm text-nera-muted-light">
                Get full access to the Starter plan at no charge. <br />
                No credit card required.
              </p>

              <button @click="handleSubscribeToFreeTrial"
                class="w-full rounded-xl border-[0.5px]! border-transparent! bg-nera-accent! p-3 text-sm font-bold text-nera-text-dark! hover:bg-nera-accent-hover! transition-colors! focus:outline-none">
                Activate free trial
              </button>

              <button @click="closeModal"
                class="mt-3 w-full rounded-xl border-[0.5px]! border-nera-light-border-strong! bg-transparent! p-3 text-sm font-medium text-nera-light-text-strong hover:bg-nera-light-hover! transition-colors focus:outline-none">
                Maybe later
              </button>
            </template>

            <template v-else-if="trialState === 'success'">
              <div
                class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500 text-xl">
                <i class="fas fa-check"></i>
              </div>
              <DialogTitle class="text-xl font-semibold text-nera-text-light">
                You're all set!
              </DialogTitle>
              <p class="mt-2 mb-8 text-sm text-nera-muted-light">
                Your free trial is now active. Enjoy full access to the Starter
                plan.
              </p>
              <button @click="closeModal"
                class="w-full rounded-xl bg-nera-accent py-2 text-sm font-semibold mt-3 text-nera-text-dark hover:bg-nera-accent-hover transition-colors">
                Get started
              </button>
            </template>

            <template v-else-if="trialState === 'error'">
              <div
                class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10 text-red-400 text-xl">
                <i class="fas fa-times"></i>
              </div>
              <DialogTitle class="text-xl font-semibold text-nera-text-light">
                Something went wrong
              </DialogTitle>
              <p class="mt-2 mb-8! text-sm text-nera-muted-light">
                We couldn't activate your trial. Please try again or contact
                support.
              </p>
              <button @click="handleSubscribeToFreeTrial"
                class="w-full rounded-xl border-[0.5px]! border-transparent! bg-nera-accent! p-3 text-sm font-bold text-nera-text-dark! hover:bg-nera-accent-hover! transition-colors! focus:outline-none">
                Try again
              </button>
              <button @click="closeModal"
                class="mt-3 not-only-of-type:w-full rounded-xl border-[0.5px]! border-nera-light-border! bg-transparent! p-3 text-sm font-medium text-nera-muted-light hover:bg-nera-light-hover! transition-colors focus:outline-none">
                Dismiss
              </button>
            </template>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, defineExpose } from "vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionRoot,
  TransitionChild,
} from "@headlessui/vue";
import { useAuthStore } from "../stores/auth";

const authStore = useAuthStore();

const openModal = () => {
  isTrialModalOpen.value = true;
};

defineExpose({
  openModal,
});

// --- free trial modal ---
const isTrialModalOpen = ref(false);
const trialState = ref("confirm"); // 'confirm' | 'success' | 'error'

const closeModal = () => {
  isTrialModalOpen.value = false;
  setTimeout(() => {
    trialState.value = "confirm";
  }, 300);
};

const handleSubscribeToFreeTrial = async () => {
  try {
    const response = await fetch("/api/subscriptions/create", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document.querySelector('[name="csrf-token"]')?.content || "",
      },
      body: JSON.stringify({ plan_name: "Starter" }),
    });

    if (!response.ok) throw new Error("Failed to subscribe to free trial");
    await authStore.fetchCurrentUser();
    trialState.value = "success";
  } catch (error) {
    console.error(error);
    trialState.value = "error";
  }
};
</script>
